import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import AutoAwesomeMotionOutlinedIcon from '@mui/icons-material/AutoAwesomeMotionOutlined'
import FlipOutlinedIcon from '@mui/icons-material/FlipOutlined'
import NumbersOutlinedIcon from '@mui/icons-material/NumbersOutlined'
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import DeferredNumericInput from './DeferredNumericInput'
import {
    applySceneArrayModifierPreview,
    clearSceneArrayModifierPreview,
    createSceneMirrorCopies,
    setSceneArrayModifierPreview,
    setSceneObjectQuantity,
} from '../../actions/sceneActions'

const getBaseId = (value) => {
    const id = String(value || '')
    return id.includes('__') ? id.split('__')[0] : id
}

const normalizeIds = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean)
    if (typeof value === 'string' && value) return [value]
    return []
}

const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value) || min))

const SceneGuestEditPanel = ({ permissions = {}, limits = {} }) => {
    const dispatch = useDispatch()
    const scene = useSelector((state) => ({
        selectedModel: state.scene?.selectedModel || null,
        selectedModelPaths: state.scene?.selectedModelPaths || [],
        selectedModels: state.scene?.selectedModels || {},
        productsMetaById: state.scene?.productsMetaById || {},
        modifierPreviews: state.scene?.modifierPreviews || {},
        modelPaths: state.scene?.modelPaths || [],
    }), shallowEqual)
    const [quantity, setQuantity] = useState(1)
    const [arrayCount, setArrayCount] = useState(2)
    const [relative, setRelative] = useState(true)
    const [offset, setOffset] = useState({ x: 1.1, y: 0, z: 0 })
    const [mirrorAxis, setMirrorAxis] = useState('x')
    const [mirrorDirection, setMirrorDirection] = useState(1)
    const [mirrorGapMM, setMirrorGapMM] = useState(100)
    const [message, setMessage] = useState('')

    const selectedPaths = useMemo(() => {
        const paths = Array.isArray(scene.selectedModelPaths)
            ? scene.selectedModelPaths.filter(Boolean)
            : []

        if (paths.length) return paths
        return scene.selectedModel ? [scene.selectedModel] : []
    }, [scene.selectedModel, scene.selectedModelPaths])

    const targetId = scene.selectedModel || selectedPaths[0] || ''
    const baseId = getBaseId(targetId)
    const productMeta = scene.productsMetaById?.[baseId] || {}
    const hasProductMeta = !!(productMeta.fullName || productMeta.name || productMeta.article)
    const targetName = hasProductMeta
        ? [productMeta.fullName || productMeta.name, productMeta.article ? `арт. ${productMeta.article}` : ''].filter(Boolean).join(' · ')
        : (targetId ? `Объект ${baseId.slice(-6)}` : 'Объект не выбран')

    const currentQuantity = useMemo(() => {
        if (!baseId) return 0

        return Object.values(scene.selectedModels || {}).reduce((sum, value) => {
            return sum + normalizeIds(value).filter((id) => getBaseId(id) === baseId).length
        }, 0)
    }, [baseId, scene.selectedModels])

    const minArray = Math.max(2, Number(limits.minArrayCount || 2))
    const maxArray = Math.max(minArray, Number(limits.maxArrayCount || 10))
    const maxSceneObjects = Math.max(1, Number(limits.maxSceneObjects || 100))
    const activePreview = scene.modifierPreviews?.arraysBySourceId?.[targetId] || null

    useEffect(() => {
        setQuantity(Math.max(1, currentQuantity || 1))
    }, [currentQuantity, targetId])

    useEffect(() => {
        if (!activePreview) return

        setArrayCount(activePreview.count || minArray)
        setRelative(activePreview.relative !== false)
        setOffset({
            x: Number(activePreview.offset?.x) || 0,
            y: Number(activePreview.offset?.y) || 0,
            z: Number(activePreview.offset?.z) || 0,
        })
    }, [activePreview, minArray])

    const updatePreview = (patch = {}, forceEnable = false) => {
        if (!targetId || !permissions.useArrayModifier) return
        if (!forceEnable && !activePreview?.enabled) return

        const next = {
            count: clamp(patch.count ?? arrayCount, minArray, maxArray),
            relative: patch.relative ?? relative,
            offset: patch.offset || offset,
            relativeSize: activePreview?.relativeSize,
            history: false,
        }
        const sceneObjectCount = Array.isArray(scene.modelPaths) ? scene.modelPaths.length : 0

        if (sceneObjectCount + next.count - 1 > maxSceneObjects) {
            setMessage(`Лимит сцены — ${maxSceneObjects} объектов`)
            return
        }

        setMessage('')
        dispatch(setSceneArrayModifierPreview(targetId, next))
    }

    const handleQuantity = () => {
        if (!permissions.changeQuantity || !targetId) return

        const result = dispatch(setSceneObjectQuantity(targetId, quantity, {
            maxQuantity: maxArray,
            maxSceneObjects,
        }))

        setMessage(result?.ok
            ? `Количество: ${result.quantity}`
            : (result?.error || 'Количество изменить не удалось'))
    }

    const handleCreateMirrorCopy = () => {
        if (!permissions.useMirrorModifier || !selectedPaths.length) return

        const result = dispatch(createSceneMirrorCopies(selectedPaths, mirrorAxis, {
            direction: mirrorDirection,
            gapMM: mirrorGapMM,
            maxSceneObjects,
        }))

        if (result?.ok) {
            const count = Array.isArray(result.copyIds) ? result.copyIds.length : 1
            setMessage(count > 1
                ? `Создано зеркальных копий: ${count}`
                : `Зеркальная копия по ${mirrorAxis.toUpperCase()} создана и выделена`)
            return
        }

        setMessage(result?.error || 'Не удалось создать зеркальную копию')
    }

    const handleApplyArray = () => {
        if (!activePreview?.enabled) return
        dispatch(applySceneArrayModifierPreview(targetId))
        setMessage('Массив применён')
    }

    const handleCancelArray = () => {
        dispatch(clearSceneArrayModifierPreview(targetId))
        setMessage('Предпросмотр массива отменён')
    }

    const hasTools = permissions.changeQuantity || permissions.useArrayModifier || permissions.useMirrorModifier

    if (!hasTools) {
        return (
            <div className="sceneGuestEditPanel__empty">
                Владелец оставил сцену только для просмотра.
            </div>
        )
    }

    return (
        <div className="sceneGuestEditPanel">
            <div className="sceneGuestEditPanel__selection">
                <span>Выбранный объект</span>
                <strong>{targetName}</strong>
                <small>{targetId ? 'Кликните по другому объекту прямо в сцене, чтобы выбрать его.' : 'Сначала выберите объект в сцене.'}</small>
            </div>

            {permissions.changeQuantity ? (
                <section className="sceneGuestEditPanel__section">
                    <div className="sceneGuestEditPanel__sectionTitle">
                        <NumbersOutlinedIcon />
                        <div><strong>Количество</strong><span>Текущих экземпляров: {currentQuantity}</span></div>
                    </div>
                    <div className="sceneGuestEditPanel__inline">
                        <DeferredNumericInput
                            value={quantity}
                            min={1}
                            max={maxArray}
                            precision={0}
                            integer
                            allowNegative={false}
                            disabled={!targetId}
                            ariaLabel="Количество экземпляров"
                            onCommit={(value) => setQuantity(clamp(Math.round(value), 1, maxArray))}
                        />
                        <button type="button" onClick={handleQuantity} disabled={!targetId}>Применить</button>
                    </div>
                    <small>Введите число полностью. Оно фиксируется после Enter или выхода из поля. Максимум для ссылки: {maxArray}; лимит сцены: {maxSceneObjects}.</small>
                </section>
            ) : null}

            {permissions.useMirrorModifier ? (
                <section className="sceneGuestEditPanel__section sceneGuestEditPanel__section--mirror">
                    <div className="sceneGuestEditPanel__sectionTitle">
                        <FlipOutlinedIcon />
                        <div>
                            <strong>Зеркальная копия</strong>
                            <span>Создаёт новый объект, не изменяя исходный</span>
                        </div>
                    </div>

                    <div className="sceneGuestEditPanel__mirrorAxis">
                        {['x', 'y', 'z'].map((axis) => (
                            <button
                                type="button"
                                key={axis}
                                className={mirrorAxis === axis ? 'is-active' : ''}
                                disabled={!selectedPaths.length}
                                onClick={() => setMirrorAxis(axis)}
                            >
                                Ось {axis.toUpperCase()}
                            </button>
                        ))}
                    </div>

                    <div className="sceneGuestEditPanel__mirrorSettings">
                        <label>
                            <span>Сторона по оси {mirrorAxis.toUpperCase()}</span>
                            <div className="sceneGuestEditPanel__mirrorDirection">
                                <button
                                    type="button"
                                    className={mirrorDirection < 0 ? 'is-active' : ''}
                                    disabled={!selectedPaths.length}
                                    onClick={() => setMirrorDirection(-1)}
                                >
                                    − {mirrorAxis.toUpperCase()}
                                </button>
                                <button
                                    type="button"
                                    className={mirrorDirection > 0 ? 'is-active' : ''}
                                    disabled={!selectedPaths.length}
                                    onClick={() => setMirrorDirection(1)}
                                >
                                    + {mirrorAxis.toUpperCase()}
                                </button>
                            </div>
                        </label>

                        <label>
                            <span>Зазор между объектами, мм</span>
                            <DeferredNumericInput
                                value={mirrorGapMM}
                                min={0}
                                max={100000}
                                precision={1}
                                allowNegative={false}
                                disabled={!selectedPaths.length}
                                ariaLabel="Зазор зеркальной копии в миллиметрах"
                                onCommit={(value) => setMirrorGapMM(Math.max(0, value))}
                            />
                        </label>
                    </div>

                    <button
                        type="button"
                        className="sceneGuestEditPanel__mirrorCreate"
                        disabled={!selectedPaths.length}
                        onClick={handleCreateMirrorCopy}
                    >
                        <FlipOutlinedIcon /> Создать зеркальную копию
                    </button>

                    <small>
                        Копия ставится рядом с исходным объектом с указанным зазором и сразу выделяется. После создания её можно отдельно перемещать, поворачивать и масштабировать.
                    </small>
                </section>
            ) : null}

            {permissions.useArrayModifier ? (
                <section className="sceneGuestEditPanel__section">
                    <div className="sceneGuestEditPanel__sectionTitle">
                        <AutoAwesomeMotionOutlinedIcon />
                        <div><strong>Массив</strong><span>Сначала предпросмотр, затем подтверждение</span></div>
                    </div>

                    <label>
                        <span>Элементов</span>
                        <DeferredNumericInput
                            value={arrayCount}
                            min={minArray}
                            max={maxArray}
                            precision={0}
                            integer
                            allowNegative={false}
                            disabled={!targetId}
                            ariaLabel="Количество элементов массива"
                            onCommit={(value) => {
                                const next = clamp(Math.round(value), minArray, maxArray)
                                setArrayCount(next)
                                updatePreview({ count: next })
                            }}
                        />
                    </label>

                    <label className="sceneGuestEditPanel__check">
                        <input
                            type="checkbox"
                            checked={relative}
                            disabled={!targetId}
                            onChange={(event) => {
                                setRelative(event.target.checked)
                                updatePreview({ relative: event.target.checked })
                            }}
                        />
                        <span>Смещение относительно размера объекта</span>
                    </label>

                    <div className="sceneGuestEditPanel__offsets">
                        {['x', 'y', 'z'].map((axis) => (
                            <label key={axis}>
                                <span>{axis.toUpperCase()}</span>
                                <DeferredNumericInput
                                    value={offset[axis]}
                                    min={-100000}
                                    max={100000}
                                    precision={2}
                                    disabled={!targetId}
                                    ariaLabel={`Смещение массива по ${axis.toUpperCase()}`}
                                    onCommit={(value) => {
                                        const next = { ...offset, [axis]: value }
                                        setOffset(next)
                                        updatePreview({ offset: next })
                                    }}
                                />
                            </label>
                        ))}
                    </div>

                    {!activePreview?.enabled ? (
                        <button
                            type="button"
                            className="sceneGuestEditPanel__preview"
                            disabled={!targetId}
                            onClick={() => updatePreview({}, true)}
                        >
                            Показать массив
                        </button>
                    ) : (
                        <div className="sceneGuestEditPanel__confirm">
                            <button type="button" className="is-apply" onClick={handleApplyArray}>
                                <CheckOutlinedIcon /> Подтвердить
                            </button>
                            <button type="button" className="is-cancel" onClick={handleCancelArray}>
                                <CloseOutlinedIcon /> Отменить
                            </button>
                        </div>
                    )}
                    <small>Ручной ввод применяется после Enter или выхода из поля. Разрешено от {minArray} до {maxArray} элементов.</small>
                </section>
            ) : null}

            {message ? <div className="sceneGuestEditPanel__message">{message}</div> : null}
        </div>
    )
}

export default React.memo(SceneGuestEditPanel)
